import React from "react";
import { UserContext } from "../../hooks/useUser";
import { useContext, useRef, useState } from "react";
import { Modal, Button, Stack } from "react-bootstrap";
import { UserProfileModalProps, Rum, Group } from "../../types";

interface ProfileDataRowProps {
    label: string;
    value: string | number;
}

export function ProfileDataRow(props: ProfileDataRowProps): JSX.Element {
    const { label, value } = props;

    return (
        <Stack direction="horizontal">
            <div className="p-2">{label}</div>
            <div className="p-2 ms-auto">{value}</div>
        </Stack>
    )
}

interface IndependentBottlerRowProps {
    label: string;
    rums: Rum[];
    needed: number;
    filter: (rum: Rum) => boolean;
}

export function IndependentBottlerRow(props: IndependentBottlerRowProps): JSX.Element {
    const { label, rums, needed, filter } = props;

    const haveAlready = useRef(-1);
    if (haveAlready.current === -1) {
        haveAlready.current = rums.filter(filter).length;
    }

    return (
        <ProfileDataRow label={label} value={`${haveAlready.current}/${needed}`} />
    )
}

interface GroupRowProps {
    group: Group;
    viewGroupRums?: (code: string) => void;
    leaveGroup?: (code: string) => void;
}

export function GroupRow(props: GroupRowProps): JSX.Element {
    const { group, viewGroupRums, leaveGroup } = props;
    const members = group.users.map(u => u.full_name || u.first_name).join(', ');

    return (
        <Stack direction="horizontal" className="align-items-center mt-2">
            <div className="p-2">
                <span className="fw-bold fs-5">{group.code}</span>
                <br />
                <span className="text-muted" style={{ fontSize: '0.8rem' }}>{members}</span>
            </div>
            <div className="p-2 ms-auto">
                <Button variant="outline-primary" size="sm" className="me-2" onClick={() => viewGroupRums && viewGroupRums(group.code)}>View</Button>
                <Button variant="outline-danger" size="sm" onClick={() => leaveGroup && leaveGroup(group.code)}>Leave</Button>
            </div>
        </Stack>
    )
}

function UserProfileModal(props: UserProfileModalProps): JSX.Element {
    const { rums, closeModal, show, groups, createGroup, joinGroup, leaveGroup, viewGroupRums } = props;
    const { logoutUser, userDetails } = useContext(UserContext);
    const [joinCode, setJoinCode] = useState<string>('');

    // personal stats
    const numSignedRums = rums.filter(rum => "approver" in rum && rum.rejected_on == null).length;
    const numSignedImmortals = rums.filter(rum => "approver" in rum && (rum as any).is_immortal && rum.rejected_on == null).length;
    const numAwaitingApproval = rums.filter(rum => !(rum as any).approved_on && rum.requested_on && rum.rejected_on == null).length;
    const untriedAvailable = rums.filter(rum => !(rum as any).approved_on && !rum.requested_on && rum.is_available).length;
    const triedUnavailable = rums.filter(rum => (rum as any).approved_on && !rum.is_available && rum.rejected_on == null).length;

    // global stats
    const availableRum = rums.filter(rum => rum.is_available).length;
    const totalImmortals = rums.filter(rum => (rum as any).is_immortal).length;
    const availableImmortals = rums.filter(rum => (rum as any).is_immortal && rum.is_available).length;

    return (
        <>
            <Modal show={show} onHide={closeModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{userDetails?.firstName} {userDetails?.lastName}</Modal.Title>
                </Modal.Header>
                <Modal.Body>

                    <Stack gap={1}>
                        <div className="p-2" style={{ borderBottom: "1px solid grey" }}>
                            <label className="fw-bold">MY STATS</label>
                            <ProfileDataRow label={"Level"} value={userDetails?.rumbustionLevel || 'Unknown'} />
                            <ProfileDataRow label={"Rums"} value={numSignedRums} />
                            <ProfileDataRow label={"Immortals"} value={numSignedImmortals} />
                            <ProfileDataRow label={"Awaiting Approval"} value={numAwaitingApproval} />
                            <ProfileDataRow label={"Untried and available"} value={untriedAvailable} />
                            <ProfileDataRow label={"Tried and unavailable"} value={triedUnavailable} />
                        </div>
                        <div className="p-2" style={{ borderBottom: "1px solid grey" }}>
                            <label className="fw-bold">OVERALL STATS</label>
                            <ProfileDataRow label={"Total Items"} value={rums.length} />
                            <ProfileDataRow label={"Available Items"} value={availableRum} />
                            <ProfileDataRow label={"Total Immortals"} value={totalImmortals} />
                            <ProfileDataRow label={"Available Immortals"} value={availableImmortals} />
                        </div>
                        <div className="p-2" style={{ borderBottom: "1px solid grey" }}>
                            <label className="fw-bold">DRINK WITH ME</label>
                            {groups && groups.map(group => (
                                <GroupRow key={group.id} group={group} viewGroupRums={viewGroupRums} leaveGroup={leaveGroup} />
                            ))}
                            <Stack direction="horizontal" className="mt-2 align-items-center">
                                <div className="p-2">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Code"
                                        maxLength={4}
                                        style={{ textTransform: 'uppercase', width: '80px' }}
                                        value={joinCode}
                                        onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                                    />
                                </div>
                                <div className="p-2 ms-auto">
                                    <Button variant="primary" size="sm" className="me-2" onClick={() => {
                                        if (joinCode.length === 4 && joinGroup) {
                                            joinGroup(joinCode);
                                            setJoinCode('');
                                        }
                                    }}>Join</Button>
                                    <Button variant="secondary" size="sm" onClick={() => createGroup && createGroup()}>Create</Button>
                                </div>
                            </Stack>
                        </div>
                        <div className="p-2" style={{ borderBottom: "1px solid grey" }}>
                            <label className="fw-bold">INDEPENDENT BOTTLERS</label>
                            <IndependentBottlerRow label={"Blackadder"} rums={rums} needed={15} filter={(rum) => rum.bevager_name.toLowerCase().includes("blackadder") && "approver" in rum && rum.rejected_on == null} />
                            <IndependentBottlerRow label={"Boutique-y"} rums={rums} needed={18} filter={(rum) => rum.bevager_name.toLowerCase().includes("boutique") && "approver" in rum && rum.rejected_on == null} />
                            <IndependentBottlerRow label={"Bristol Classic/Bristol Spirits"} rums={rums} needed={17} filter={(rum) => rum.bevager_name.toLowerCase().includes("bristol") && "approver" in rum && rum.rejected_on == null} />
                            <IndependentBottlerRow label={"Cadenhead"} rums={rums} needed={14} filter={(rum) => rum.bevager_name.toLowerCase().includes("cadenhead") && "approver" in rum && rum.rejected_on == null} />
                            <IndependentBottlerRow label={"Colours of Rum"} rums={rums} needed={14} filter={(rum) => rum.bevager_name.toLowerCase().includes("colours of rum") && "approver" in rum && rum.rejected_on == null} />
                            <IndependentBottlerRow label={"Duncan Taylor"} rums={rums} needed={8} filter={(rum) => rum.bevager_name.toLowerCase().includes("duncan") && "approver" in rum && rum.rejected_on == null} />
                            <IndependentBottlerRow label={"Hamilton"} rums={rums} needed={15} filter={(rum) => rum.bevager_name.toLowerCase().includes("hamilton") && "approver" in rum && rum.rejected_on == null} />
                            <IndependentBottlerRow label={"Holmes Cay"} rums={rums} needed={24} filter={(rum) => rum.bevager_name.toLowerCase().includes("holmes cay") && "approver" in rum && rum.rejected_on == null} />
                            <IndependentBottlerRow label={"Plantation"} rums={rums} needed={16} filter={(rum) => (rum.bevager_name.toLowerCase().includes("plantation") || rum.bevager_name.toLowerCase().includes("planteray")) && !rum.bevager_name.toLowerCase().includes("grove") && !rum.bevager_name.toLowerCase().includes("myer") && "approver" in rum && rum.rejected_on == null} />
                            <IndependentBottlerRow label={"Royal Cane"} rums={rums} needed={13} filter={(rum) => rum.bevager_name.toLowerCase().includes("royal cane") && "approver" in rum && rum.rejected_on == null} />
                            <IndependentBottlerRow label={"S.B.S"} rums={rums} needed={15} filter={(rum) => rum.bevager_name.includes("SBS") && "approver" in rum && rum.rejected_on == null} />
                            <IndependentBottlerRow label={"Samaroli"} rums={rums} needed={22} filter={(rum) => rum.bevager_name.toLowerCase().includes("samaroli") && "approver" in rum && rum.rejected_on == null} />
                            <IndependentBottlerRow label={"Velier"} rums={rums} needed={42} filter={(rum) => (rum.bevager_name.toLowerCase().includes("velier") || rum.bevager_name.toLowerCase().includes("lm&v")) && "approver" in rum && rum.rejected_on == null} />
                        </div>
                    </Stack>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="danger" onClick={logoutUser || (() => { })}>
                        Logout
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default UserProfileModal;
